import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Grid,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import LaunchRoundedIcon from '@mui/icons-material/LaunchRounded';
import RouteRoundedIcon from '@mui/icons-material/RouteRounded';
import FactCheckRoundedIcon from '@mui/icons-material/FactCheckRounded';
import { Link as RouterLink } from 'react-router-dom';
import {
  companyProfile,
  consultationOptions,
  deliveryPackages,
  workflowSteps
} from '../data/siteContent';

const initialForm = {
  companyName: '',
  contactName: '',
  contactChannel: '',
  projectType: consultationOptions.projectTypes[0],
  projectGoal: '',
  currentStage: consultationOptions.stages[0],
  launchWindow: consultationOptions.launchWindows[1],
  budgetRange: consultationOptions.budgetRanges[1],
  details: ''
};

const recommendationMap = {
  '官网改版 / 品牌站': '建议先明确品牌信息架构、咨询入口与首屏转化路径，再推进页面与表单交付。',
  '小程序 / H5 项目': '建议先锁定核心业务链路、接口依赖和异常兜底，再安排多端联调与发布验证。',
  'AI 推荐 / 增长场景': '建议先定义目标指标、埋点口径和策略范围，避免只做模型不做闭环。',
  '企业流程 / 管理平台': '建议先梳理角色权限、关键流程节点和经营看板，再进入开发阶段。',
  '接口联调 / 上线保障': '建议先确认环境、接口清单、健康检查和回滚手段，再安排上线窗口。',
  其他定制需求: '建议先用诊断清单把范围、依赖和优先级说清楚，再决定首批交付内容。'
};

const buildSummary = (form) => {
  const lines = [
    `公司名称：${form.companyName || '待补充'}`,
    `联系人：${form.contactName || '待补充'}`,
    `沟通方式：${form.contactChannel || '待补充'}`,
    `项目类型：${form.projectType}`,
    `当前阶段：${form.currentStage}`,
    `目标上线时间：${form.launchWindow}`,
    `预算范围：${form.budgetRange}`,
    `核心目标：${form.projectGoal || '待补充'}`,
    `补充说明：${form.details || '待补充'}`,
    '',
    '建议的首批动作：',
    `1. ${recommendationMap[form.projectType]}`,
    '2. 先产出诊断清单、MVP 范围和联调里程碑，确保关键链路可运行。',
    '3. 在上线前补齐构建验证、smoke 测试和回滚准备。'
  ];

  return lines.join('\n');
};

const ConsultationPage = () => {
  const [form, setForm] = useState(initialForm);
  const [snackbar, setSnackbar] = useState('');

  const summary = useMemo(() => buildSummary(form), [form]);

  const updateField = (key) => (event) => {
    setForm((current) => ({ ...current, [key]: event.target.value }));
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(summary);
      setSnackbar('需求摘要已复制');
    } catch (error) {
      setSnackbar('复制失败，请手动复制右侧摘要');
    }
  };

  const handleDownload = () => {
    const blob = new Blob([summary], { type: 'text/plain;charset=utf-8' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'tgrelease-project-brief.txt';
    link.click();
    window.URL.revokeObjectURL(url);
    setSnackbar('需求摘要已下载');
  };

  return (
    <Stack spacing={3}>
      <Paper
        sx={{
          p: { xs: 3, md: 4.5 },
          borderRadius: 4,
          color: 'white',
          background: 'linear-gradient(128deg, #0f172a 0%, #1d4ed8 56%, #38bdf8 100%)'
        }}
      >
        <Stack spacing={1.3}>
          <Typography variant="h3" sx={{ fontWeight: 800 }}>
            咨询入口与首批交付预审
          </Typography>
          <Typography sx={{ fontSize: 16.5, lineHeight: 1.8, maxWidth: 920, opacity: 0.95 }}>
            这一页用于把“想做什么、现在做到哪一步、首期先交什么”快速说清楚。
            你可以直接填写项目背景，系统会即时生成一份可复制、可下载的需求摘要。
          </Typography>
        </Stack>
      </Paper>

      <Grid container spacing={2.2}>
        <Grid item xs={12} md={7}>
          <Paper sx={{ p: 3, borderRadius: 3, border: '1px solid #dbe7ff' }}>
            <Typography variant="h5" sx={{ fontWeight: 800, mb: 2 }}>
              填写项目信息
            </Typography>
            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="公司名称" value={form.companyName} onChange={updateField('companyName')} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="联系人" value={form.contactName} onChange={updateField('contactName')} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="沟通方式"
                  placeholder="电话 / 微信 / 邮箱"
                  value={form.contactChannel}
                  onChange={updateField('contactChannel')}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField select fullWidth label="项目类型" value={form.projectType} onChange={updateField('projectType')}>
                  {consultationOptions.projectTypes.map((item) => (
                    <MenuItem key={item} value={item}>
                      {item}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField select fullWidth label="当前阶段" value={form.currentStage} onChange={updateField('currentStage')}>
                  {consultationOptions.stages.map((item) => (
                    <MenuItem key={item} value={item}>
                      {item}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  fullWidth
                  label="目标上线时间"
                  value={form.launchWindow}
                  onChange={updateField('launchWindow')}
                >
                  {consultationOptions.launchWindows.map((item) => (
                    <MenuItem key={item} value={item}>
                      {item}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  select
                  fullWidth
                  label="预算范围"
                  value={form.budgetRange}
                  onChange={updateField('budgetRange')}
                >
                  {consultationOptions.budgetRanges.map((item) => (
                    <MenuItem key={item} value={item}>
                      {item}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="核心目标"
                  placeholder="例如：一个月内上线新版官网并补齐咨询转化入口"
                  value={form.projectGoal}
                  onChange={updateField('projectGoal')}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  minRows={4}
                  label="补充说明"
                  placeholder="补充当前问题、接口依赖、已有团队、是否已有设计稿等"
                  value={form.details}
                  onChange={updateField('details')}
                />
              </Grid>
            </Grid>

            <Alert severity="info" sx={{ mt: 2 }}>
              当前版本提供前台预审能力：可即时生成需求摘要、首批动作建议和交付方向，方便进入下一轮沟通。
            </Alert>
          </Paper>
        </Grid>

        <Grid item xs={12} md={5}>
          <Paper sx={{ p: 3, borderRadius: 3, border: '1px solid #dbe7ff', height: '100%' }}>
            <Typography variant="h5" sx={{ fontWeight: 800, mb: 2 }}>
              即时生成的需求摘要
            </Typography>
            <Paper
              sx={{
                p: 2,
                borderRadius: 2,
                background: '#0f172a',
                color: '#e2e8f0',
                minHeight: 280,
                overflow: 'auto'
              }}
            >
              <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{summary}</pre>
            </Paper>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2} sx={{ mt: 2 }}>
              <Button variant="contained" startIcon={<ContentCopyRoundedIcon />} onClick={handleCopy}>
                复制摘要
              </Button>
              <Button variant="outlined" startIcon={<DownloadRoundedIcon />} onClick={handleDownload}>
                下载摘要
              </Button>
            </Stack>

            <Box sx={{ mt: 2.5, p: 2, borderRadius: 2, bgcolor: '#eff6ff' }}>
              <Typography sx={{ fontWeight: 800, mb: 1.1 }}>建议下一步</Typography>
              <Typography color="text.secondary" sx={{ lineHeight: 1.8 }}>
                {recommendationMap[form.projectType]}
              </Typography>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      <Grid container spacing={2.2}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, borderRadius: 3, border: '1px solid #dbe7ff', height: '100%' }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
              <RouteRoundedIcon color="primary" />
              <Typography variant="h6" sx={{ fontWeight: 800 }}>
                首批可交付方向
              </Typography>
            </Stack>
            <Stack spacing={1.4}>
              {deliveryPackages.map((item) => (
                <Box key={item.title} sx={{ p: 1.8, borderRadius: 2.5, bgcolor: '#f8fbff', border: '1px solid #e5eefc' }}>
                  <Typography sx={{ fontWeight: 800 }}>
                    {item.title} · {item.duration}
                  </Typography>
                  <Typography color="text.secondary" sx={{ mt: 0.8, lineHeight: 1.75 }}>
                    {item.summary}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, borderRadius: 3, border: '1px solid #dbe7ff', height: '100%' }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
              <FactCheckRoundedIcon color="primary" />
              <Typography variant="h6" sx={{ fontWeight: 800 }}>
                项目推进节奏
              </Typography>
            </Stack>
            <Stack spacing={1.4}>
              {workflowSteps.map((item) => (
                <Box key={item.title} sx={{ p: 1.8, borderRadius: 2.5, bgcolor: '#f8fbff', border: '1px solid #e5eefc' }}>
                  <Typography sx={{ fontWeight: 800 }}>{item.title}</Typography>
                  <Typography color="text.secondary" sx={{ mt: 0.8, lineHeight: 1.75 }}>
                    {item.description}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.8, color: 'primary.main', fontWeight: 700 }}>
                    输出：{item.output}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Paper>
        </Grid>
      </Grid>

      <Paper sx={{ p: 3, borderRadius: 3, border: '1px solid #dbe7ff' }}>
        <Grid container spacing={2.2} alignItems="center">
          <Grid item xs={12} md={8}>
            <Typography variant="h5" sx={{ fontWeight: 800 }}>
              已确认的公开信息
            </Typography>
            <Typography color="text.secondary" sx={{ mt: 1, lineHeight: 1.8 }}>
              {companyProfile.legalName} · {companyProfile.city} · 官方站点 {companyProfile.domain} · 备案号 {companyProfile.icp}
            </Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <Stack direction={{ xs: 'column', sm: 'row', md: 'column' }} spacing={1.2}>
              <Button component={RouterLink} to="/services" variant="contained" startIcon={<LaunchRoundedIcon />}>
                查看服务清单
              </Button>
              <Button component={RouterLink} to="/api-web" variant="outlined">
                打开 API Web
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      <Snackbar
        open={Boolean(snackbar)}
        autoHideDuration={2200}
        onClose={() => setSnackbar('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" variant="filled" onClose={() => setSnackbar('')}>
          {snackbar}
        </Alert>
      </Snackbar>
    </Stack>
  );
};

export default ConsultationPage;
